import { useQuery } from '@tanstack/react-query';
import { Wrench, Clock, CalendarClock, AlertTriangle, Plus } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PageWrapper from '@/components/layout/PageWrapper';
import { useAuth } from '@/auth/AuthContext';
import { repairRequestsApi, pmAssignmentsApi } from '@/api';
import { cn } from '@/lib/utils';

interface SummaryCardProps {
  title: string;
  value: number | undefined;
  icon: React.ElementType;
  isLoading: boolean;
  colorClass?: string;
  description?: string;
}

function SummaryCard({
  title,
  value,
  icon: Icon,
  isLoading,
  colorClass = 'text-primary',
  description,
}: SummaryCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={cn('h-5 w-5', colorClass)} />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-8 w-16 animate-pulse rounded bg-muted" />
        ) : (
          <div className="text-3xl font-bold text-foreground">
            {value ?? '—'}
          </div>
        )}
        {description && (
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { currentUser, hasRole } = useAuth();

  if (currentUser?.role === 'ext_vendor') {
    return <Navigate to="/ordered-parts" replace />;
  }

  // Fetch open repair requests
  const { data: openRequests, isLoading: loadingOpen } = useQuery({
    queryKey: ['repairRequests', 'dashboard-open'],
    queryFn: () => repairRequestsApi.list({ status: 'New' }),
  });

  const { data: inProgressRequests, isLoading: loadingInProgress } = useQuery({
    queryKey: ['repairRequests', 'dashboard-in-progress'],
    queryFn: () => repairRequestsApi.list({ status: 'In-Progress' }),
  });

  const showPm = !hasRole(['submitter', 'ext_vendor', 'accounting']);

  const { data: activeAssignments, isLoading: loadingPms } = useQuery({
    queryKey: ['pmAssignments', 'active'],
    queryFn: () => pmAssignmentsApi.list({ active: true }),
    enabled: showPm,
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneWeek = new Date(today.getTime() + 7 * 86_400_000);

  const pmsDueThisWeek = activeAssignments?.filter((a) => {
    if (!a.nextDueDate) return false;
    const due = new Date(a.nextDueDate);
    return due >= today && due <= oneWeek;
  });

  const overduePs = activeAssignments?.filter((a) => {
    if (!a.nextDueDate) return false;
    return new Date(a.nextDueDate) < today;
  });

  const isSubmitter = hasRole(['submitter']);
  const navigate = useNavigate();

  return (
    <PageWrapper
      title="Dashboard"
      subtitle={`Welcome back, ${currentUser?.name ?? ''}!`}
      actions={
        <Button size="sm" onClick={() => navigate('/repair-requests?new=1')}>
          <Plus className="mr-1.5 h-4 w-4" />
          New Repair Request
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          title="Open Requests"
          value={openRequests?.total}
          icon={Wrench}
          isLoading={loadingOpen}
          colorClass="text-blue-500"
          description={isSubmitter ? 'Your submitted requests' : 'Across all equipment'}
        />
        <SummaryCard
          title="In Progress"
          value={inProgressRequests?.total}
          icon={Clock}
          isLoading={loadingInProgress}
          colorClass="text-yellow-500"
          description="Currently being worked on"
        />
        {!isSubmitter && (
          <>
            <SummaryCard
              title="PMs Due This Week"
              value={pmsDueThisWeek?.length}
              icon={CalendarClock}
              isLoading={loadingPms}
              colorClass="text-orange-500"
              description="Scheduled maintenance coming up"
            />
            <SummaryCard
              title="Overdue PMs"
              value={overduePs?.length}
              icon={AlertTriangle}
              isLoading={loadingPms}
              colorClass="text-red-500"
              description="Maintenance past due date"
            />
          </>
        )}
      </div>

      {/* Recent activity placeholder */}
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-semibold text-foreground">
          Recent Activity
        </h3>
        <Card>
          <CardContent className="pt-6">
            {loadingOpen ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : (openRequests?.items?.length ?? 0) === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">
                No recent activity to show.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {openRequests?.items?.slice(0, 5).map((req) => (
                  <li key={req.id} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {req.equipment?.unitNumber ?? 'Unknown Unit'} — {req.problem.slice(0, 60)}
                        {req.problem.length > 60 ? '…' : ''}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {req.equipment?.company ?? '—'} · {new Date(req.requestDate).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="ml-4 shrink-0 inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                      New
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </PageWrapper>
  );
}
